/** biome-ignore-all lint/suspicious/noExplicitAny: <é necessário refatorar o arquivo todo para parar de usar any> */
import { BaseModel, FetchResponse } from "@/interfaces/api";
import { PlainFunction, PlainObject, RequireOnlyOne } from "@/interfaces/common";
import { SimpleEvent, UseFormProps } from "@/interfaces/form";
import { showToast } from "@/providers";
import { APIError, FormError, handleAppError, isNextRedirectException, validations } from "@/utils";
import type { FormValidations } from "@/utils";
import { yupResolver } from "@hookform/resolvers/yup";
import { capitalize, get, isEqual, map } from "lodash-es";
import { type ChangeEvent, type FormEvent, startTransition, useEffect, useState } from "react";
import {
  type Control,
  type FieldErrors,
  SetValueConfig,
  type UseFormRegisterReturn,
  type UseFormReset,
  type UseFormSetValue,
  type UseFormSetError,
  type UseFormTrigger,
  type UseFormWatch,
  useForm as useReactForm,
} from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as yup from "yup";

export interface UseFormControl {
  register: (name: string) => UseFormRegisterReturn<string>;
  errors: FieldErrors<any>;
  isFetching: boolean;
  getValue: (key: any) => any;
  watch: UseFormWatch<any>;
  setValue: UseFormSetValue<any>;
  setError: UseFormSetError<any>;
  trigger: UseFormTrigger<any>;
  controller: Control<any, any, any>;
  reset: UseFormReset<any>;
  onChange: (event: SimpleEvent) => void;
}

function entries<T extends PlainObject>(data: T) {
  return Object.entries(data) as [keyof T, T[keyof T]][];
}

const relationSeparator = ":";
const isRelation = (field: string) => field.includes(relationSeparator);

export function useForm<T extends PlainObject | null, K extends string>({
  action,
  fields,
  options: { redirect, onSuccess, onError, onSubmitError, crud, idParamName = "id", onFetch } = {},
  handleSubmit,
  initialValues,
}: RequireOnlyOne<UseFormProps<T, K>, "action" | "handleSubmit">) {
  useEffect(() => {
    if (crud) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const schema = yup.object(
    entries(fields).reduce(
      (data, [key, { validation }]) => {
        const path = validation ?? key;

        if (typeof path !== "string") {
          return { ...data, [key]: path };
        }

        if (!(path in validations)) {
          throw new Error(`Invalid form validation "${path}" for field "${key}"`);
        }

        return { ...data, [key]: validations[path as FormValidations] };
      },
      {} as Record<K, yup.AnySchema>,
    ),
  );

  const {
    handleSubmit: validateForm,
    register,
    formState,
    getValues,
    reset,
    watch,
    setValue,
    trigger,
    control: controller,
    setError,
  } = useReactForm<any>({
    resolver: yupResolver(schema),
    defaultValues: initialValues,
    shouldFocusError: true,
  });

  const { errors, isSubmitting, isDirty } = formState;

  const [entity, setEntity] = useState<PlainObject | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [hasError, setHasError] = useState(false);

  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;

  const id = params[idParamName] as string | undefined;
  const isCreate = !id || id === "novo";
  const isForm = id && pathname.endsWith(id);

  // prettier-ignore
  const isSubmittable =
    isForm &&
    !!watch() &&
    (() => {
      const values = getValues();

      if (!entity) {
        return Object.keys(values).some((key) => !!schema.fields[key as K] && values[key as K]);
      }

      return !isEqual(entity, values);
    })();

  const formAction = (event?: FormEvent) => {
    event?.preventDefault();

    try {
      startTransition(() => {
        const validateHandler = validateForm(
          async (data) => {
            const payload: PlainObject = { ...data };

            for (const [key, { omit }] of entries(fields)) {
              if (omit) {
                delete payload[key];
              }

              if (isRelation(key)) {
                const [relation, field] = key.split(relationSeparator);

                payload[`${relation}${capitalize(field)}`] = get(payload, key);
              }
            }

            const submit = (action ?? handleSubmit) as PlainFunction<Promise<T | FetchResponse<T>>>;

            try {
              const response = await submit(payload, { redirect });

              if (response?.error) {
                throw new APIError({ message: response.error }, response.statusCode);
              }

              if (redirect && action) {
                return;
              }

              if (redirect) {
                navigate(redirect);
              }

              await onSuccess?.(action ? response?.data : response);
            } catch (error) {
              if (isNextRedirectException(error)) {
                throw error;
              }

              if (onSubmitError?.(error)) {
                return;
              }

              // Trata erros de validação do backend
              if (error instanceof FormError) {
                onError?.(map(error.properties, "property"));

                error.properties.forEach(({ property, errorMessages }) => {
                  setError(property as K, {
                    type: "server",
                    message: errorMessages.join(", "),
                  });
                });
              }

              handleAppError(error);
            }
          },
          (validationErrors) => {
            onError?.(Object.keys(validationErrors));

            showToast({
              type: "error",
              title: "Há dados inválidos.",
              content: "Verifique os campos obrigatórios.",
            });
          },
        );

        validateHandler();
      });
    } catch (error) {
      // Error handling for form action
      console.error(error);
    }
  };

  if (!entity && !isFetching && crud && params.id && !isCreate) {
    setIsFetching(true);
  }

  const fetchData = async () => {
    if (!crud || isCreate) {
      return;
    }

    if (!id) {
      throw new Error("Missing ID parameter");
    }

    setIsFetching(true);

    try {
      const actions: ((item: PlainObject) => void)[] = [];

      const relations = Object.keys(fields).reduce((records, key) => {
        if (isRelation(key)) {
          records.add(key.split(relationSeparator)[0]);

          actions.push((item) => {
            item[key as keyof BaseModel] = get(item, key.split(relationSeparator));
          });
        }

        return records;
      }, new Set<string>());

      const data = await crud.getOne(id, { relations: Array.from(relations) });

      setEntity(data);

      actions.forEach((callback) => callback(data));

      reset(data);
      onFetch?.(data as unknown as T, reset);
    } catch (error) {
      setHasError(true);
      handleAppError(error);
    } finally {
      setIsFetching(false);
    }
  };

  const getValue = (key: K) => {
    return getValues()[key];
  };

  const observables = <Record<K, (event: any) => void>>{};

  const observe = (field: K, callback: (event: ChangeEvent<HTMLInputElement>) => void) => {
    observables[field] = callback;
  };

  const handleRegister = (name: string) => {
    const registration = register(name);
    const originalOnChange = registration.onChange;

    registration.onChange = async (event) => {
      observables[name as K]?.(event);

      return originalOnChange(event);
    };

    return registration;
  };

  const handleOnChange = (event: SimpleEvent) => {
    const { name, value } = event.target;

    let config: SetValueConfig = {};

    if (formState.isSubmitted) {
      config = { shouldValidate: true, shouldDirty: true, shouldTouch: true };
    }

    setValue(name as K, value, config);
    observables[name as K]?.(event);
  };

  return {
    control: {
      register: handleRegister,
      errors,
      isFetching,
      getValue,
      watch,
      setValue,
      setError,
      trigger,
      controller,
      reset,
      onChange: handleOnChange,
    },
    action: formAction,
    isSubmitting,
    isDirty,
    hasError,
    formState,
    values: getValue,
    entity,
    isFetching,
    observe,
    isSubmittable: id ? isSubmittable : true,
  };
}
